/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { PbHousePlanFinderListItem } from '@gh/builder-shared';
import {
  ContactCustomerListItemDto,
  JobChangeOrderListDto,
  JobChangeOrderListItemDto,
  JobChangeOrderListMasterInfoDto,
  JobDppDetailsDto,
  JobListFilterDto,
  JobListItemDto,
  JobQuickCreateRequestDto,
  JobQuickCreateResponseDto,
  JobSpecClauseDto,
  JobSpecDetailsDto,
  JobSpecParagraphDto,
  JobSpecSectionDto,
  JobSpecTemplateFilterDto,
  JobSpecTemplateListItemDto,
  JobUpdateDPPDto,
  SpecTemplateClauses,
  SpecTemplateParagraphIs,
  SpecTemplateSectionIs,
  SpecTemplateTextIs,
  UserDetailsShortDto,
} from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  FormStateSnapshot,
  formStateSnapshot,
  getFormStateSnapshotValue,
  setEntityField,
  TextTypes,
} from '@gh/common-shared';
import {
  AutocompleteItem,
  EntityVersion,
  ID,
  PrintEntryPoint,
  WithEntityState,
  WithVersion,
} from '@gh/common-shared-data';
import { WithOptimisticLock } from '@gh/core-data';
import { createTreeHelper } from '@gh/core-ui-ext/tree';
import { cast, currentDate } from '@gh/core-util';
import { compact, find, isEmpty, isNil, omit, pick, without } from 'lodash';

export type JobListItem = JobListItemDto;
export type JobListFilter = JobListFilterDto;

export function getPreviewTypeLabel(entryPoint: PrintEntryPoint): string {
  return entryPoint === PrintEntryPoint.Preview ? 'lbl_link_to_preview_job_specification' :
    'lbl_link_to_job_specification';
}

export type JobQuickDetails = {
  jobId: ID;
  contact: JobCustomer;
  salesperson: JobSalesPerson;
  owner1: string;
  owner2: string;
  owner3: string;
  siteName: string;
  lotNumber: string;
  blockNumber: string;
  suburb: string;
  streetNumber: string;
  street: string;
  postCode: string;
  city: string;
  state: string;
  mapReference: string;
  reference: string;
  priceBook: AutocompleteItem;
  housePlan: PbHousePlanFinderListItem;
  housePlanName: string;
  baseDesign: AutocompleteItem;
  jobMemo: string;

  // prefilled from labels
  salesFolderName: string;
  quotationDescription: string;

  version: EntityVersion; // contact version

  // UI only fields
  isQqShow: boolean;
};

export type JobCustomer = ContactCustomerListItemDto;
export type JobSalesPerson = UserDetailsShortDto;
export type JobAdmin = UserDetailsShortDto;

export type JobQuickCreateResponse = JobQuickCreateResponseDto;

export const jobQuickDetailsToCreateRequestDto = (details: JobQuickDetails) => <JobQuickCreateRequestDto>({
  ...pick(details, [
    'jobId',
    'owner1', 'owner2', 'owner3',
    'siteName',
    'lotNumber', 'blockNumber', 'suburb',
    'streetNumber', 'street', 'postCode', 'city', 'state',
    'mapReference', 'reference',
    'housePlanName',
    'jobMemo',
    'salesFolderName', 'quotationDescription',
  ]),
  contactId: details.contact.contactId,
  salespersonId: details.salesperson.userId,
  priceBookId: details.priceBook.id,
  housePlanId: details.housePlan.housePlanId,
  baseDesignId: details.baseDesign.id,
  createdLocal: currentDate(),
  version: details.contact.version,
});

export enum JobSpecSelectionLevel { Paragraph = 1, Section = 2}

export type JobSpecSelection = {
  readonly level?: JobSpecSelectionLevel;
  readonly paragraph?: JobSpecParagraphDetails;
  readonly section?: JobSpecSectionDetails;
};

export const jobSpecSelectionToTreePath = ({ level, paragraph, section }: JobSpecSelection) =>
  level ? compact([paragraph && paragraph.id, section && section.id]) : void 0;

export type JobSpecTemplateListItem = JobSpecTemplateListItemDto;
export type JobSpecTemplateFilter = JobSpecTemplateFilterDto;
export type JobSpecTemplateFinder = {
  jobSpecTemplate?: JobSpecTemplateListItem;
};
export type JobClause = JobSpecClauseDto & { quoteChange?: JobSpecQuoteChange };

export type JobSpecInfo = {
  specTemplateId: ID;
  jobNumber: string;
  isFinalized: boolean;
  isNotProceeding: boolean;
  name: string;
  customerName: string;
  contactId: ID;
  previewLink: string;
  fileName: string;
};

export type JobSpecParagraphInfo = {
  name: string;
  text: string;
  paragraphIs: SpecTemplateParagraphIs;
  textIs: SpecTemplateTextIs;
  isTextIncluded: boolean;
  isParagraphIncluded: boolean;
};

export type JobSpecSectionInfo = {
  name: string;
  text: string;
  clausesAre: SpecTemplateClauses;
  sectionIs: SpecTemplateSectionIs;
  textIs: SpecTemplateTextIs;
  clauseValue: string;
  clauses: JobClause[];
  clause?: JobClause;
  isSectionIncluded: boolean;
  isTextIncluded: boolean;
  isTextWarning: boolean;
  isHidden: boolean;
};

export type JobSpecClause = JobSpecClauseDto;

export type JobSpecDetails = WithVersion<{
  masterInfo: FormStateSnapshot<JobSpecInfo>;
  paragraphs: JobSpecParagraphDetails[];
  isContractSigned: boolean;
}>;

export type JobSpecParagraphDetails = {
  id: ID;
  isOqc: boolean;
  info: FormStateSnapshot<JobSpecParagraphInfo>;
  children: JobSpecSectionDetails[];
  quoteChanges: JobSpecQuoteChange[];
  isDraggable: boolean;
};

export type JobSpecSectionDetails = {
  id: ID;
  info: FormStateSnapshot<JobSpecSectionInfo>;
  jobItemOrComponentId: ID;
  isComponent: boolean;
  isDraggable: boolean;
};

export type JobSpecQuoteChange = WithEntityState<JobSpecSectionDto & { isDraggable: boolean }>;

const quoteChangeFromDto = (dto: JobSpecSectionDto) => <JobSpecQuoteChange>({ ...dto, isDraggable: true });
const quoteChangeToDto = (model: JobSpecQuoteChange) => <JobSpecSectionDto>omit(model, ['isDraggable']);

export const jobSpecSectionFromDto = (dto: JobSpecSectionDto) => <JobSpecSectionDetails>({
  id: dto.specSectionId,
  ...pick(dto, ['jobItemOrComponentId', 'isComponent']),
  info: formStateSnapshot(cast<JobSpecSectionInfo>(pick(
    dto,
    [
      'name', 'text', 'textIs', 'sectionIs', 'clausesAre', 'isTextWarning',
      'clauseValue', 'clauses', 'isSectionIncluded', 'isTextIncluded', 'isHidden',
    ]))),
  isDraggable: false,
});

export const jobSpecParagraphFromDto = (dto: JobSpecParagraphDto) => <JobSpecParagraphDetails>({
  id: dto.specParagraphId,
  ...pick(dto, ['isOqc']),
  info: formStateSnapshot(cast<JobSpecParagraphInfo>(pick(
    dto,
    ['name', 'text', 'isTextIncluded', 'isParagraphIncluded', 'isTextWarning', 'paragraphIs', 'textIs']))),
  children: !dto.sections || dto.isOqc ? [] : dto.sections.map(jobSpecSectionFromDto),
  quoteChanges: dto.isOqc && dto.sections ? (dto.sections.map(quoteChangeFromDto) || []) : void 0,
});

export const jobSpecSectionToDto = (model: JobSpecSectionDetails) => {
  const info = getFormStateSnapshotValue(model.info);
  return <JobSpecSectionDto>({
    specSectionId: model.id,
    ...pick(model, ['textIs', 'jobItemOrComponentId', 'isComponent']),
    ...omit(info, 'clause'),
    clauses: info.clauses.map((clause) => omit(clause, 'quoteChange')),
  });
};

export const jobSpecParagraphToDto = (model: JobSpecParagraphDetails) =>
  <JobSpecParagraphDto>({
    specParagraphId: model.id,
    ...pick(model, ['isOqc']),
    ...getFormStateSnapshotValue(model.info),
    sections: model.isOqc ? model.quoteChanges.map(quoteChangeToDto) : model.children.map(jobSpecSectionToDto),
  });

export const jobSpecFromDto = (dto: JobSpecDetailsDto) => {
  const paragraphs = dto.paragraphs.map(jobSpecParagraphFromDto);
  const qcParagraph = find(paragraphs, { isOqc: true });

  return <JobSpecDetails>({
    masterInfo: formStateSnapshot(dto),
    paragraphs: qcParagraph && !isEmpty(qcParagraph.quoteChanges) ? paragraphs : without(paragraphs, qcParagraph),
    // duplicate quotes changes paragraph to ease finding of quote changes
    quoteChanges: qcParagraph ? qcParagraph : void 0,
    isContractSigned: dto.isContractSigned,
    version: dto.version,
  });
};

export const jobSpecToDto = ({ masterInfo, paragraphs, version }: JobSpecDetails) =>
  <JobSpecDetailsDto>({
    ...getFormStateSnapshotValue(masterInfo),
    paragraphs: paragraphs.map(jobSpecParagraphToDto),
    version,
  });

export const jobSpecQuoteChangeToSection = (qc: JobSpecQuoteChange): JobSpecSectionDetails =>
  ({ ...jobSpecSectionFromDto(qc), isDraggable: true });

export const jobSpecQuoteChangeFromSection = (qc: JobSpecSectionDetails): JobSpecQuoteChange =>
  ({
    specSectionId: qc.id,
    ...pick(qc, ['jobItemOrComponentId', 'isComponent']),
    ...getFormStateSnapshotValue(qc.info),
    isDraggable: true,
  });

export const JOB_SPEC_PARAGRAPH_ENTITY_CONFIG: EntityConfig<JobSpecParagraphInfo> = {
  text: {
    required: ({ isParagraphIncluded, textIs }) => isParagraphIncluded && textIs === SpecTemplateTextIs.Mandatory,
  },
};
export const JOB_SPEC_SECTION_ENTITY_CONFIG: EntityConfig<JobSpecSectionInfo> = {
  text: {
    required: ({ isSectionIncluded, textIs }) => isSectionIncluded && textIs === SpecTemplateTextIs.Mandatory,
  },
};
export const validateJobSpecParagraphInfo = entityValidatorByEntityConfig(JOB_SPEC_PARAGRAPH_ENTITY_CONFIG);
export const validateJobSpecSectionInfo = entityValidatorByEntityConfig(JOB_SPEC_SECTION_ENTITY_CONFIG);

export const getJobSpecQuoteChangeId = (section: JobSpecQuoteChange) => section.specSectionId;
export const jobSpecQuoteChangeArr = createArrayHelper(getJobSpecQuoteChangeId);
export const jobSpecTreeHelper = createTreeHelper(['children']);

export const JOB_SPEC_QUOTE_CHANGE_ENTITY_CONFIG: EntityConfig<JobSpecQuoteChange> = {
  text: { type: TextTypes.Text255 },
};
export const jobSpecQuoteChangeFieldState = fieldStateByEntityConfig(JOB_SPEC_QUOTE_CHANGE_ENTITY_CONFIG);
export const validateJobSpecQuoteChangeField = fieldValidatorByEntityConfig(JOB_SPEC_QUOTE_CHANGE_ENTITY_CONFIG);
export const setJobSpecQuoteChangeField = composeSetters(
  setEntityField,
  fieldValidationSetter(validateJobSpecQuoteChangeField));
export type JobDppDetailsGeneral = JobDppDetailsDto;
export type JobUpdateDPP = JobUpdateDPPDto;
export type JobDppDetails = WithOptimisticLock<{
  id: ID;
  general: JobDppDetailsGeneral;
}>;

export type JobCoMasterInfo = JobChangeOrderListMasterInfoDto;
export type JobCoListItem = JobChangeOrderListItemDto;
export type JobCo = JobChangeOrderListDto;

export type JobCoMasterInfoDetails = {
  siteAddress: string;
  contactName: string;
  isFinalized?: boolean;
  isNotProceeding?: boolean;
};

export type JobCoList = {
  masterInfo: FormStateSnapshot<JobCoMasterInfoDetails>;
  changeOrders: JobCoListItem[];
};

export const getJobCoListItemId = (item: JobCoListItem) => item.id;

export const jobCoFromDto = (dto: JobCo) => <JobCoList>({
  masterInfo: formStateSnapshot({
    ...dto.job,
    job: dto.job,
  }),
  changeOrders: dto.changeOrders,
});

export const getEmptyJobCo = (): JobCoList => jobCoFromDto({
  job: <any>void 0,
  changeOrders: [],
});

export const isNotQcParagraph = (node: JobSpecParagraphDetails) => isNil(node.quoteChanges);
